import { X } from "lucide-react";
import React from "react";
import { useModalFocusTrap } from "../../components/useModalFocusTrap";

export interface AlphaSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidthClass?: string;
  closeDisabled?: boolean;
  description?: string;
  spacious?: boolean;
}

/**
 * v7 modal shell for the alpha profile settings surfaces. Same contract as
 * the legacy SettingsModal internal to SettingsPage.
 */
const AlphaSettingsModal: React.FC<AlphaSettingsModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidthClass = "max-w-4xl",
  closeDisabled = false,
  description,
  spacious = false,
}) => {
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  useModalFocusTrap({ dialogRef, isOpen, onClose, closeDisabled });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-v7-ink/60 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-appearance="alpha"
        className={`${spacious ? "flex flex-col" : ""} w-full ${maxWidthClass} max-h-[90vh] overflow-hidden rounded-v7-popover border border-v7-ink/15 bg-v7-white font-v7-sans shadow-2xl`}
      >
        <div className={`flex justify-between ${spacious ? "shrink-0 gap-4 items-start px-6 pt-6 pb-5" : "items-center border-b border-v7-ink/10 px-5 py-4"}`}>
          <div>
            <h2 id={titleId} className={`${spacious ? "text-[22px] leading-[30px]" : "text-base leading-tight"} font-bold text-v7-ink`}>
              {title}
            </h2>
            {description && <p className="mt-2 text-sm leading-5 text-v7-ink-soft">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            className="rounded-v7-control p-1 text-v7-ink/45 transition-colors hover:bg-v7-paper hover:text-v7-ink disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className={`overflow-y-auto ${spacious ? "min-h-0 px-6 pb-6" : "max-h-[calc(90vh-64px)] p-5"}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AlphaSettingsModal;
